import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'

import { Alert } from '../../common/Alert'
import { QUERY_PARAMS } from '../../../constants'
import { apiFetch } from '../../../utils/apiRequest'

/**
 * Downloads the current bank-reconciliation selection as a CSV (opens in Excel). It exports ONLY the
 * transactions the user has selected on screen — passed in as `paymentIds` — and formats them per the
 * "Line Item Detail" toggle: individual rows when on, day-grouped rows when off.
 */
export const useReconciliationCsvExport = () => {
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)

  const accountId = searchParams.get(QUERY_PARAMS.ACCOUNT_ID)
  const statementMonth = searchParams.get(QUERY_PARAMS.STATEMENT_MONTH)
  // "Line Item Detail" toggle. Matches the screen default (on unless explicitly 'false'); when off, the
  // export is the day-grouped view instead of individual transactions.
  const lineItems = searchParams.get(QUERY_PARAMS.RECONCILIATION_LINE_ITEMS) !== 'false'

  const exportCsv = async (paymentIds: string[]) => {
    if (!accountId) {
      Alert.error('Please select an account')
      return
    }

    if (!paymentIds?.length) {
      Alert.error('Select at least one transaction to export')
      return
    }

    setIsLoading(true)
    try {
      const monthOfYear = dayjs(statementMonth || Date())
        .startOf('month')
        .format('YYYY-MM-DD')

      const body = JSON.stringify({
        accountId,
        lineItems,
        paymentIds
      })

      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/reconciliation/export`, {
        method: 'POST',
        body
      })

      if (!response.ok) {
        throw new Error('Failed to export bank reconciliation')
      }

      const data = await response.text()
      const blobData = new Blob([data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blobData)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `bank_reconciliation_${lineItems ? 'line_items' : 'grouped'}_${monthOfYear}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      Alert.error((err as Error).message)
    } finally {
      setIsLoading(false)
    }
  }

  return { exportCsv, isLoading, canExport: !!accountId }
}
